/**
 * A simple, language-agnostic string tokenizer. This tokenizer will divide a string up into an array of
 * tokens where each token is either:
 * - a name: a string of characters that are A-Z, a-z, a number or an underscore
 * - an operator: a string of characters that are not name characters
 * - a string: a string of any characters surrounded by matching quotes (", ' or `)
 *
 *  example: given the string "Thread_Id IN ('one 1', 'two 2')", will produce the following tokens:
 *  ["Thread_Id", "IN", "(", "'one 1", "," "'two 2'", ")"]
 *
 * @param str The string to be tokenized
 * @returns string[] of tokens
 */
export function simpleTokenize(str: string) {
  // array of all the tokens to be returned
  const tokens: string[] = [];

  // the current character as we progress through
  // the input string
  type Char = string | undefined;
  let currChar: Char;

  // this will be defined when we encounter the opening
  // of a string, as the string characters
  type StringChar = "'" | '"' | '`';
  let openingStrChar: StringChar | undefined;

  // characters which have been accumulated as we
  // progress through the input string, which have
  // yet to terminate as a single token
  let accumulatedChars: string[] = [];

  // move accumulated chars from accumulated array
  // to token array and clear out accumulated array
  const captureAccumulated = () => {
    if (accumulatedChars.length) {
      tokens.push(accumulatedChars.join(''));
      accumulatedChars = [];
    }
  };

  const isStringChar = (char: Char): char is StringChar =>
    char === "'" || char === '"' || char === '`';

  const isWhitespace = (char: Char) => char?.trim() === '';

  // tests whether a character is a "name" character
  // A-Z, a-z, a number or an underscore
  const isNameChar = (char: Char) => char && /[A-Za-z_0-9]/.test(char);

  for (let i = 0; i < str.length; i += 1) {
    currChar = str[i];
    if (!currChar) {
      // fail safe
      continue;
    }

    // currently consuming chars in a string
    if (openingStrChar && currChar !== openingStrChar) {
      // currently in a string, so just push the current char
      accumulatedChars.push(currChar);
      continue;
    }

    // terminating current string
    if (openingStrChar && currChar === openingStrChar) {
      // terminating a string, add current string terminating
      // char, and capture the string token
      accumulatedChars.push(currChar);
      captureAccumulated();

      openingStrChar = undefined;
      continue;
    }

    // starting a new string
    if (isStringChar(currChar)) {
      // we starting a new string, so convert any chars we've
      // accumulated to a token
      captureAccumulated();

      accumulatedChars.push(currChar);

      openingStrChar = currChar;
      continue;
    }

    if (isWhitespace(currChar)) {
      // we hit whitespace, so convert any chars we've accumulated
      // to a token
      captureAccumulated();
      continue;
    }

    if (isNameChar(currChar)) {
      captureAccumulated();

      while (isNameChar(currChar)) {
        accumulatedChars.push(currChar);
        i += 1;
        currChar = str[i];
      }

      captureAccumulated();

      i -= 1; // we went one too far, back up i
      continue;
    }

    if (!isNameChar(currChar)) {
      captureAccumulated();

      while (
        currChar &&
        !isNameChar(currChar) &&
        !isWhitespace(currChar) &&
        !isStringChar(currChar)
      ) {
        accumulatedChars.push(currChar);
        i += 1;
        currChar = str[i];
      }

      captureAccumulated();

      i -= 1; // we went one too far, back up i
      continue;
    }
  }

  // capture any straggling accumlated chacters.
  // This could happen for an unterminated string - but we aren't
  // validating syntax here
  captureAccumulated();

  return tokens;
}
